import { useEffect, useState } from "react";
import {
    CircularProgress,
    Box,
    Typography,
    ImageList,
    ImageListItem,
    Dialog,
    IconButton,
    useTheme,
    useMediaQuery
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useTranslation } from "react-i18next";
import { IImage } from "@/services/api/Image/types";
import imagesApi from "@/services/api/Image";
import ImageGalleryContainer from "@/components/comercial/ImageGallery/ImageGalleryContainer";
import {ImageEntity} from "@/components/general/Images/types";



interface ImageGalleryContainerApiProps {
    entityId?: string;
    entityType?: ImageEntity;
    images?: IImage[];
}

const ImageGalleryContainerApi = ({ entityId, entityType, images: preloadedImages }: ImageGalleryContainerApiProps) => {
    const { t } = useTranslation();
    const theme = useTheme();

    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

    const [images, setImages] = useState<IImage[]>([]);
    const [loading, setLoading] = useState<boolean>(!preloadedImages);
    const [error, setError] = useState<boolean>(false);
    const [selectedImage, setSelectedImage] = useState<string | null>(null);

    const getGridColumns = () => {
        if (isMobile) return 1;
        if (isTablet) return 2;
        return 3;
    };

    useEffect(() => {
        if (preloadedImages) {
            setImages(preloadedImages);
            setLoading(false);
            return;
        }

        const fetchStoreImages = async () => {
            try {
                setLoading(true);
                setError(false);
                const response = await imagesApi.getImagesByEntity(entityType!, entityId!);
                setImages(Array.isArray(response) ? response : []);
            } catch (err) {
                console.error("Erro ao buscar imagens da loja:", err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (entityId) {
            fetchStoreImages();
        }
    }, [entityId, preloadedImages]);

    if (loading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={35} />
            </Box>
        );
    }

    if (error) {
        return (
            <Typography color="error" textAlign="center" sx={{ p: 3, fontWeight: 500 }}>
                {t("storePage.management.details.errorLoadingImages", "Erro ao carregar imagens.")}
            </Typography>
        );
    }

    if (images.length === 0) {
        return (
            <Typography color="text.secondary" textAlign="center" sx={{ p: 4, fontStyle: "italic" }}>
                {t("storePage.management.details.noImages", "Nenhuma imagem cadastrada para esta loja.")}
            </Typography>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                    px: isMobile ? 1 : 0
                }}
            >
                <ImageGalleryContainer style={{ width: "100%" }}>
                    <ImageList
                        cols={getGridColumns()}
                        gap={isMobile ? 12 : 16}
                        sx={{
                            width: "100%",
                            maxWidth: "1200px",
                            height: "auto",
                            margin: "0 auto",
                            overflowY: "hidden"
                        }}
                    >
                        {images.map((item) => (
                            <ImageListItem
                                key={item.id}
                                onClick={() => setSelectedImage(item.imageUrl)}
                                sx={{
                                    overflow: "hidden",
                                    borderRadius: 2,
                                    cursor: "pointer",
                                    transition: "transform 0.25s ease-in-out, box-shadow 0.25s",
                                    "&:hover": {
                                        transform: isMobile ? "none" : "scale(1.02)",
                                        boxShadow: isMobile ? "none" : theme.shadows[4]
                                    }
                                }}
                            >
                                <img
                                    src={item.thumbnailUrl}
                                    alt="Miniatura da imagem da loja"
                                    loading="lazy"
                                    style={{
                                        width: "100%",
                                        height: "auto",
                                        aspectRatio: "16 / 9",
                                        objectFit: "cover",
                                        display: "block"
                                    }}
                                />
                            </ImageListItem>
                        ))}
                    </ImageList>
                </ImageGalleryContainer>
            </Box>

            <Dialog
                open={Boolean(selectedImage)}
                onClose={() => setSelectedImage(null)}
                maxWidth="md"
                fullWidth
                fullScreen={isMobile}
                scroll="body"
                PaperProps={{
                    sx: {
                        bgcolor: isMobile ? "rgba(0, 0, 0, 0.95)" : "transparent",
                        boxShadow: "none",
                        overflow: "hidden",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                    }
                }}
            >
                {selectedImage && (
                    <Box
                        sx={{
                            position: "relative",
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            width: "100%",
                            height: "100%",
                            p: isMobile ? 2 : 0
                        }}
                    >
                        <IconButton
                            onClick={() => setSelectedImage(null)}
                            sx={{
                                position: "absolute",
                                top: isMobile ? 16 : 8,
                                right: isMobile ? 16 : 8,
                                color: "#fff",
                                zIndex: 10,
                                bgcolor: "rgba(0, 0, 0, 0.6)",
                                "&:hover": { bgcolor: "rgba(0, 0, 0, 0.8)" }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                        <img
                            src={selectedImage}
                            alt="Imagem da loja em tamanho real"
                            style={{
                                maxWidth: "100%",
                                maxHeight: isMobile ? "80vh" : "90vh",
                                objectFit: "contain",
                                borderRadius: isMobile ? "8px" : "4px",
                                boxShadow: isMobile ? "none" : "0px 4px 20px rgba(0,0,0,0.5)"
                            }}
                        />
                    </Box>
                )}
            </Dialog>
        </>
    );
};

export default ImageGalleryContainerApi;